import { TransactionReportItem } from "features/generals/domain/entities/transactionReports/TransactionReportResponseEntity";
import { NumberFormat } from "utils/format/NumberFormat";
import { formatCurrency } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { GeneralStatCards } from "../interfaces/IGenerals";
import { GeneralCurrencyMappers } from "./GeneralCurrencyMappers";

export class GeneralStatCardMappers {
  private constructor() {}

  static formatChangeRate = (rate?: number) => {
    if (rate === undefined || rate === null) return "";
    const sign = rate >= 0 ? "+" : "";
    return `${sign}${rate.toFixed(1)}%`;
  };

  static defaultStatCards = (
    currencyCode: string = "VND"
  ): GeneralStatCards => ({
    todayRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(
        0,
        true,
        true,
        GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
      ),
      change: "",
      trend: "up",
    },
    monthRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(
        0,
        true,
        true,
        GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
      ),
      change: "",
      trend: "up",
    },
    previousMonthRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(
        0,
        true,
        true,
        GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
      ),
      change: "",
      trend: "up",
    },
    todayTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
    monthTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
    previousMonthTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
  });

  static buildStatCards = (
    transactionReportData: TransactionReportItem[],
    currencyCode: string
  ): GeneralStatCards => {
    if (!transactionReportData || transactionReportData.length === 0) {
      return this.defaultStatCards(currencyCode);
    }

    const today = transactionReportData?.[0];
    const thisMonth = transactionReportData?.[1];
    const previousMonth = transactionReportData?.[2];

    return {
      todayRevenue: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? formatCurrency(
              today.revenue ?? 0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            )
          : formatCurrency(
              0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            ),
        change:
          today?.revenueChangeRate !== undefined &&
          today?.revenueChangeRate !== null
            ? this.formatChangeRate(today.revenueChangeRate)
            : "",
        trend: (today?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      monthRevenue: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? formatCurrency(
              thisMonth.revenue ?? 0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            )
          : formatCurrency(
              0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            ),
        change:
          thisMonth?.revenueChangeRate !== undefined &&
          thisMonth?.revenueChangeRate !== null
            ? this.formatChangeRate(thisMonth.revenueChangeRate)
            : "",
        trend: (thisMonth?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      previousMonthRevenue: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? formatCurrency(
              previousMonth.revenue ?? 0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            )
          : formatCurrency(
              0,
              true,
              true,
              GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
            ),
        change:
          previousMonth?.revenueChangeRate !== undefined &&
          previousMonth?.revenueChangeRate !== null
            ? this.formatChangeRate(previousMonth.revenueChangeRate)
            : "",
        trend: (previousMonth?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      todayTransactions: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? NumberFormat.currencyFormat(today.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          today?.quantityChangeRate !== undefined &&
          today?.quantityChangeRate !== null
            ? this.formatChangeRate(today.quantityChangeRate)
            : "",
        trend: (today?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
      monthTransactions: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? NumberFormat.currencyFormat(thisMonth.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          thisMonth?.quantityChangeRate !== undefined &&
          thisMonth?.quantityChangeRate !== null
            ? this.formatChangeRate(thisMonth.quantityChangeRate)
            : "",
        trend: (thisMonth?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
      previousMonthTransactions: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? NumberFormat.currencyFormat(previousMonth.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          previousMonth?.quantityChangeRate !== undefined &&
          previousMonth?.quantityChangeRate !== null
            ? this.formatChangeRate(previousMonth.quantityChangeRate)
            : "",
        trend: (previousMonth?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
    };
  };
}
